import type { CardData } from '../components/IntakeCard'
import { LABEL, fmt, pieceName } from './types'

/** The columns an intake card is rebuilt from (Recent intakes and the /print/intake/:id reprint). */
export const INTAKE_CARD_COLS = 'id, intake_no, type, status, counterparty_name, cost_cents, credit_cents, owner_cost_cents, split_pct, consignor_minimum_cents, payout_method, sourced_by, sourced_from, whose_piece, brand, item, size, condition, deal_note, measurements, authenticated, trade_no, signed_by, created_at, rehearsal, consignors(first_name, last_name)'

export interface IntakeRow {
  id: string; intake_no: number | null; type: 'bought' | 'consigned' | 'traded'; status: string; counterparty_name: string
  cost_cents: number; credit_cents: number | null; owner_cost_cents: number | null; split_pct: number | null; consignor_minimum_cents: number | null
  payout_method: string | null; sourced_by?: string | null; sourced_from?: string | null; whose_piece: CardData['whose']; brand: string; item: string; size: string; condition: CardData['cond']
  deal_note: string; measurements: string; authenticated: boolean; trade_no: number | null; signed_by: string | null; created_at: string; rehearsal?: boolean
  consignors?: { first_name: string; last_name: string } | null
}

/** Masih's own pieces are typed in as bought but recorded on his consignment record; the card still says bought. */
export const isMasihOwn = (r: IntakeRow) => r.type === 'consigned' && r.whose_piece === 'masih' && r.owner_cost_cents != null

/** What the counter showed for the piece: Bought / Consigned / Traded, and from whom. */
export function intakeHow(r: IntakeRow): CardData['how'] { return isMasihOwn(r) ? 'bought' : r.type }
export function intakeFrom(r: IntakeRow) {
  if (r.sourced_by === 'shane') return `Shane Sears${r.sourced_from ? `, from ${r.sourced_from}` : ''}`
  if (r.type === 'consigned' && !isMasihOwn(r)) return `${r.consignors?.first_name ?? ''} ${r.consignors?.last_name ?? ''}`.trim() || 'Consignor'
  return r.counterparty_name || (r.type === 'traded' ? 'Trade customer' : '')
}

/** Consigned deal text; the minimum is optional (shipped-in consignments may have none). */
export function consignedDeal(fee: number, minDollars: number | null) {
  return minDollars && minDollars > 0 ? `${fee}% fee, min ${fmt(minDollars)}` : `${fee}% fee, no minimum`
}
export function consignedSentence(fee: number, minDollars: number | null) {
  return `Starchive fee ${fee}%, consignor gets ${100 - fee}% of sale, ${minDollars && minDollars > 0 ? `floor ${fmt(minDollars)}` : 'no floor'}.`
}

/** Rebuild the printed card from the intake row, the same card the counter printed on confirm. */
export function cardFromRow(r: IntakeRow): CardData {
  const how = intakeHow(r)
  const masih = isMasihOwn(r)
  const fee = r.split_pct != null ? Math.round((100 - Number(r.split_pct)) * 100) / 100 : 0
  const min = r.consignor_minimum_cents != null ? r.consignor_minimum_cents / 100 : null
  const tradeCredit = (r.credit_cents ?? r.cost_cents) / 100
  const payout = r.payout_method ? (LABEL.payout as Record<string, string>)[r.payout_method] ?? '' : ''
  const deal = how === 'traded' ? `${fmt(tradeCredit)} trade credit`
    : how === 'consigned' ? consignedDeal(fee, min)
    : masih ? fmt((r.owner_cost_cents ?? 0) / 100)
    : r.sourced_by === 'shane' ? `${fmt(r.cost_cents / 100)}, sourced by Shane, no payout`
    : `${fmt(r.cost_cents / 100)}${payout ? ', ' + payout : ''}`
  const sentence = r.deal_note || (how === 'traded' ? `Trade-in credited against trade #${r.trade_no ?? ''}` : how === 'consigned' ? consignedSentence(fee, min) : '')
  return {
    how,
    date: new Date(r.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit', timeZone: 'America/New_York' }),
    id: r.signed_by ?? '', from: intakeFrom(r), deal, whose: r.whose_piece, sentence,
    what: pieceName(r.brand, r.item, r.size ?? ''), cond: r.condition,
    intakeNo: r.intake_no ?? '—', tradeNo: r.trade_no, authenticated: !!r.authenticated, measurements: r.measurements ?? '',
  }
}
